import { redirect } from "@tanstack/react-router";
import { signOut } from "firebase/auth";
import { auth } from "@/firebase/app";
import { DASHBOARD_HOME } from "@/lib/routes";
import { isAccountDeletedError } from "@/lib/trpcErrors";
import {
  getUserWorkspaces,
  isAccessibleWorkspace,
} from "@/lib/workspaces";
import { pickEntryWorkspace, rememberWorkspaceId } from "@/lib/lastWorkspace";
import { queryClient, trpc } from "@/trpc/client";

/**
 * The auth + workspace-membership gate every `/…/$workspaceId` route needs,
 * whether it renders inside `AppShell` (`/dashboard/w/$workspaceId`) or
 * outside it (onboarding, #601). Extracted from `$workspaceId.tsx`'s
 * `beforeLoad` (#592) so the three onboarding routes reuse the exact same
 * decision instead of a copy each — a workspace that is accessible/redirects
 * one way in the dashboard must behave identically from onboarding.
 *
 * Prefetches `getCurrentUser` for the one caller that reads it: the onboarding
 * updates screen, so the consent signal (`isVerified`) is available on its first
 * render rather than after a flash of its unconfirmed state. The dashboard
 * routes share this gate but currently consume nothing from that query — kept
 * for the onboarding path, which genuinely benefits.
 *
 * Throws a `redirect` for every terminal outcome (signed out, deleted account,
 * inaccessible workspace); returns normally when the caller's own `workspaceId`
 * param is accessible or auth is still resolving.
 */
export async function guardWorkspaceAccess({
  context,
  params,
  preload = false,
}: {
  // Narrowed to what this gate actually reads, not the full `AuthState`:
  // `user` is only ever tested for presence, so `unknown` is enough and no
  // caller (or test) has to construct a whole Firebase `User`.
  context: { auth: { isLoading: boolean; user: unknown } };
  params: { workspaceId: string };
  /**
   * TanStack's own flag for "this run is a preload, not a navigation". The
   * router passes it on every `beforeLoad`; it defaults to `false` here only
   * so the tests (and any direct caller) can omit it.
   */
  preload?: boolean;
}): Promise<void> {
  // Don't fetch authenticated data until Firebase auth has resolved — see
  // `$workspaceId.tsx` for the full explanation of the reload-timing race.
  if (context.auth.isLoading) return;

  // Auth has resolved and there is no user: send them to sign in. Inside
  // `/dashboard` this is redundant (the parent route's `beforeLoad` already
  // does it, `dashboard.tsx:20-22`) and harmless. Outside it — the three
  // root-level onboarding routes (#601) have no such parent — it is the only
  // thing standing between a signed-out visitor and an unhandled error: every
  // read below goes out with an empty Authorization header, `getUserWorkspaces`
  // rejects with an auth error that is not `isAccountDeletedError`, and the
  // rethrow at the bottom of this function surfaces it as the router's error
  // boundary instead of the login page. Reachable from a stale onboarding link
  // or a session that expired mid-flow.
  //
  // No return-to on purpose: this app has none. `/login`'s own `beforeLoad`
  // unconditionally bounces an authenticated visitor to `DASHBOARD_HOME` and
  // never reads a search param, and `dashboard.tsx`'s redirect passes none
  // either — so a `redirect` param here would be silently ignored, and
  // honouring it would mean changing the post-login destination for every
  // entry point, not just this one. Deliberately out of scope for #601;
  // matching the existing behaviour is what keeps the two gates identical.
  if (!context.auth.user) {
    throw redirect({ to: "/login" });
  }

  try {
    await queryClient.ensureQueryData(
      trpc.authenticatedRouterFunction.getCurrentUser.queryOptions(),
    );
  } catch {
    // Couldn't read the consent signal — let the caller render anyway. Harmless
    // on both paths, for different reasons: the onboarding updates screen has
    // its own `useQuery` on `getCurrentUser` and will retry, so a miss costs
    // only the flash of its unconfirmed state; the dashboard routes have no
    // reader at all since `$workspaceId.tsx` dropped its `getCurrentUser`
    // query, so there the prefetch is currently unread and a miss costs
    // nothing either way. Nothing gates on it on either path.
  }

  let workspaces;
  try {
    workspaces = await getUserWorkspaces();
  } catch (err) {
    if (isAccountDeletedError(err)) {
      try {
        await signOut(auth);
      } catch {
        /* ignore */
      }
      throw redirect({ to: "/account-deleted" });
    }
    throw err;
  }

  if (isAccessibleWorkspace(workspaces, params.workspaceId)) {
    // Remembered HERE, on the admit path, rather than anywhere a workspace id
    // is merely mentioned (#1014): this is the one place that has established
    // the user really belongs to it, and it covers every way into a workspace
    // at once — the menu, a bookmark, a deep link, the post-create navigation
    // — because every `/…/$workspaceId` route runs this gate. Writing it from
    // the menu alone would forget a workspace entered by URL.
    // …but NOT on a preload run. `defaultPreload: "intent"` (src/router.tsx)
    // makes the router call this `beforeLoad` when a workspace link is merely
    // hovered or focused, and a hover is not a visit: without this test,
    // pointing at another workspace in the menu and moving on would rewrite
    // the memory, and the next bare `/dashboard` would open a workspace the
    // user never entered. The write belongs to the committed navigation, which
    // is the only run that leaves `preload` false.
    if (!preload) {
      rememberWorkspaceId(params.workspaceId);
    }
    return;
  }

  // The requested workspace is not the caller's. Prefer the one this browser
  // was last in over the first in creation order: a user who normally works in
  // their second blog and follows a stale link should land back in it, not be
  // quietly moved to their first. `pickEntryWorkspace` re-checks membership
  // against this very list, so the memory can only ever choose among
  // workspaces the caller already passed the check for.
  const fallback = pickEntryWorkspace(workspaces);
  if (!fallback) {
    throw redirect({ to: "/" });
  }

  throw redirect({
    to: DASHBOARD_HOME,
    params: { workspaceId: fallback.workspaceId },
  });
}
